import { Link, usePage } from "@inertiajs/react"
import { ArrowRight, MapPin, TriangleAlert } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { formatMoney } from "@/features/user/money"
import { t } from "@/lib/i18n"

type InviteParticipant = {
  id: number
  name: string
  initials: string
  isMine: boolean
}

type InviteOrder = {
  id: number
  name: string
  quantity: number
  paymentStatus: string
  lineTotal: string
  currency: string
}
type InvitePaymentAttempt = {
  id: number
  status: string
  paymentMethod: string
  totalAmount: string
  checkoutUrl: string | null
} | null
type InviteBill = {
  id: number
  status: string
  currentUserCanPay: boolean
  pendingPaymentAttempt: InvitePaymentAttempt
  participants?: InviteParticipant[]
  orders: InviteOrder[]
}
type InvitePayload = {
  token: string
  locationName: string
  tableName: string | null
  stateMessage: string | null
  activeBills: InviteBill[]
}
type Props = {
  error?: string
  invite?: InvitePayload
}

export default function UserInvitesShow() {
  const { error, invite } = usePage<Props>().props

  if (!invite || error) {
    return (
      <UserLayout>
        <div
          data-testid="invite-invalid-view"
          className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-16 text-center"
        >
          <span className="flex size-14 items-center justify-center rounded-full bg-warning/12 text-warning">
            <TriangleAlert className="size-7" />
          </span>
          <h1 className="text-xl font-semibold tracking-tight">
            {t("ui.react.pages.user.invites.show.invite_invalid")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {error || t("ui.user.invites.process_failed")}
          </p>
        </div>
      </UserLayout>
    )
  }

  return (
    <UserLayout locationName={invite.locationName}>
      <div data-testid="invite-context-view" className="mx-auto w-full max-w-2xl px-4 py-6">
        <header className="space-y-2 py-2">
          {invite.tableName && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-subtle px-2.5 py-1 text-xs font-semibold text-brand-ink">
              <MapPin className="size-3.5" />
              {t("ui.user.common.table_label", { name: invite.tableName })}
            </span>
          )}
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("ui.user.invites.welcome", { name: invite.locationName })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("ui.user.invites.continue_order")}
          </p>
        </header>

        {invite.stateMessage && (
          <p className="mt-3 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
            {invite.stateMessage}
          </p>
        )}

        {invite.activeBills.length > 0 && (
          <div className="mt-5 space-y-3">
            {invite.activeBills.map((bill) => (
              <section
                key={bill.id}
                data-testid={`invite-bill-${bill.id}`}
                className="rounded-2xl border bg-card p-4 shadow-xs"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold tracking-tight">
                      {t("ui.react.pages.user.invites.show.bill")}
                      {bill.id}
                    </p>
                    {bill.participants && bill.participants.length > 0 && (
                      <p className="truncate text-xs text-muted-foreground">
                        {bill.participants
                          .map((participant) =>
                            participant.isMine ? t("ui.user.common.me") : participant.name
                          )
                          .join(", ")}
                      </p>
                    )}
                  </div>
                  {bill.participants && bill.participants.length > 0 && (
                    <AvatarStack participants={bill.participants} />
                  )}
                </div>

                {bill.orders.length > 0 ? (
                  <ul className="mt-3 divide-y divide-border/60">
                    {bill.orders.map((order) => (
                      <li key={order.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="min-w-0 truncate">
                          <span className="tabular-nums text-muted-foreground">{order.quantity}×</span>{" "}
                          {order.name}
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">
                          {formatMoney(order.lineTotal, { currency: order.currency })}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t("ui.react.pages.user.invites.show.no_orders")}
                  </p>
                )}

                {bill.pendingPaymentAttempt?.checkoutUrl && (
                  <Button asChild variant="outline" size="sm" className="mt-3">
                    <a href={bill.pendingPaymentAttempt.checkoutUrl}>
                      {t("ui.react.pages.user.invites.show.continue_payment")}
                    </a>
                  </Button>
                )}
              </section>
            ))}
          </div>
        )}

        {/*
          `replace`, not a push. This screen's URL is the invite token — the one
          printed on the table — and it is a door, not a place to come back to.
          `User::InvitesController#show` re-runs the whole entrance on every GET:
          it re-registers the visit, and once that visit ends (bill settled,
          table released) `approval_required?` turns true again and the same URL
          answers by sending the diner off to ask for the table to be opened.
          A back press from the menu is not worth that.

          Paired with `EntryAskPanel`'s replace on the ask: between them the
          printed token's URLs leave the stack entirely, so back from the menu
          goes wherever the diner was before they scanned — nothing at all, for a
          QR opened into a fresh tab, which is the honest answer.
        */}
        <Button asChild size="lg" className="mt-6 w-full" data-testid="invite-continue-button">
          <Link href="/dinein/menu" replace>
            {t("ui.react.pages.user.invites.show.continue_menu")}
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </UserLayout>
  )
}

function AvatarStack({ participants }: { participants: InviteParticipant[] }) {
  const visible = participants.slice(0, 4)
  const extra = participants.length - visible.length
  return (
    <div className="flex shrink-0 items-center -space-x-2" aria-hidden="true">
      {visible.map((participant) => (
        <span
          key={participant.id}
          title={participant.name}
          className={cn(
            "flex size-7 items-center justify-center rounded-full border-2 border-card text-xs font-semibold",
            participant.isMine ? "bg-brand-subtle text-brand-ink" : "bg-muted text-foreground"
          )}
        >
          {participant.initials}
        </span>
      ))}
      {extra > 0 && (
        <span className="flex size-7 items-center justify-center rounded-full border-2 border-card bg-muted text-xs font-semibold text-muted-foreground">
          +{extra}
        </span>
      )}
    </div>
  )
}
